import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Badge, Button, Table, Alert, Modal, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faWrench, 
  faPlus, 
  faEdit, 
  faTrash,
  faEye,
  faSignOutAlt,
  faHome,
  faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { maintenanceService } from '../../services/maintenanceService';
import { userService } from '../../services/userService';
import { tenantService } from '../../services/tenantService';

const MaintenanceRequests = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  
  const [requests, setRequests] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const maintenanceTypes = {
    plumbing: 'Plumbing Issues',
    electrical: 'Electrical Issues',
    hvac: 'HVAC/Heating & Cooling',
    appliance: 'Appliance Repair',
    structural: 'Structural Issues',
    pest_control: 'Pest Control',
    cleaning: 'Cleaning Services',
    landscaping: 'Landscaping',
    security: 'Security Issues',
    other: 'Other'
  };

  const priorityColors = {
    low: 'success',
    medium: 'warning',
    high: 'danger',
    urgent: 'danger'
  };

  const statusColors = {
    pending: 'warning',
    in_progress: 'info',
    completed: 'success',
    cancelled: 'secondary'
  };

  const statusLabels = {
    pending: 'Pending',
    in_progress: 'In Progress',
    completed: 'Completed',
    cancelled: 'Cancelled'
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const profile = await userService.getUserProfile();
      setUserProfile(profile);
      
      const tenants = await tenantService.getTenantsByCompany(profile.companyId);
      let tenantData = tenants.find(t => t.authUid === profile.uid);
      
      // If not found by authUid, try to find by email
      if (!tenantData) {
        console.log('Tenant not found by authUid, looking by email:', profile.email);
        tenantData = tenants.find(t => t.email === profile.email);
      }
      
      if (!tenantData) {
        setError('Tenant profile not found');
        return;
      }
      
      setTenant(tenantData);
      
      const requestsData = await maintenanceService.getMaintenanceRequestsByTenant(tenantData.id);
      setRequests(requestsData);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load maintenance requests');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleDelete = (request) => {
    setSelectedRequest(request);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await maintenanceService.deleteMaintenanceRequest(selectedRequest.id);
      setRequests(requests.filter(r => r.id !== selectedRequest.id));
      setShowDeleteModal(false);
      setSelectedRequest(null);
    } catch (error) {
      console.error('Error deleting request:', error);
      setError('Failed to delete request');
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">Maintenance Requests</h2>
          <p className="text-muted mb-0">Manage your maintenance requests</p>
        </div>
        <div className="d-flex gap-2">
          <Button as={Link} to="/tenant/maintenance/new" variant="primary">
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            New Request
          </Button>
          <Button as={Link} to="/tenant/dashboard" variant="outline-secondary">
            <FontAwesomeIcon icon={faHome} className="me-2" />
            Dashboard
          </Button>
          <Button variant="outline-danger" onClick={handleLogout}>
            <FontAwesomeIcon icon={faSignOutAlt} className="me-2" />
            Logout
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-primary bg-opacity-10 p-3 rounded">
                    <FontAwesomeIcon icon={faWrench} className="text-primary" size="lg" />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h4 className="mb-1">{requests.length}</h4>
                  <p className="text-muted mb-0">Total Requests</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-warning bg-opacity-10 p-3 rounded">
                    <FontAwesomeIcon icon={faWrench} className="text-warning" size="lg" />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h4 className="mb-1">{requests.filter(r => r.status === 'pending').length}</h4>
                  <p className="text-muted mb-0">Pending</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-info bg-opacity-10 p-3 rounded">
                    <FontAwesomeIcon icon={faWrench} className="text-info" size="lg" />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h4 className="mb-1">{requests.filter(r => r.status === 'in_progress').length}</h4>
                  <p className="text-muted mb-0">In Progress</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-success bg-opacity-10 p-3 rounded">
                    <FontAwesomeIcon icon={faWrench} className="text-success" size="lg" />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h4 className="mb-1">{requests.filter(r => r.status === 'completed').length}</h4>
                  <p className="text-muted mb-0">Completed</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Maintenance Requests Table */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-transparent border-0">
          <h5 className="mb-0">
            <FontAwesomeIcon icon={faWrench} className="text-primary me-2" />
            My Maintenance Requests
          </h5>
        </Card.Header>
        <Card.Body>
          {requests.length === 0 ? (
            <div className="text-center py-4">
              <FontAwesomeIcon icon={faWrench} className="text-muted mb-3" size="3x" />
              <h5 className="text-muted">No maintenance requests found</h5>
              <p className="text-muted">You haven't submitted any maintenance requests yet.</p>
              <Button as={Link} to="/tenant/maintenance/new" variant="primary">
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Create Your First Request
              </Button>
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Request ID</th>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((request) => (
                  <tr key={request.id}>
                    <td>
                      <strong className="text-primary">{request.requestId}</strong>
                    </td>
                    <td>
                      <div>
                        <strong>{request.title}</strong>
                        {request.unitNumber && (
                          <div className="text-muted small">Unit: {request.unitNumber}</div>
                        )}
                      </div>
                    </td>
                    <td>
                      <Badge bg="secondary">
                        {maintenanceTypes[request.type] || request.type}
                      </Badge>
                    </td>
                    <td>
                      <Badge bg={priorityColors[request.priority]}>
                        {request.priority}
                      </Badge>
                    </td>
                    <td>
                      <Badge bg={statusColors[request.status]}>
                        {statusLabels[request.status]}
                      </Badge>
                    </td>
                    <td>
                      <div>
                        <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                        {request.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                      </div>
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button 
                          as={Link} 
                          to={`/tenant/maintenance/${request.id}`} 
                          variant="outline-primary" 
                          size="sm"
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </Button>
                        {request.status === 'pending' && (
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={() => handleDelete(request)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Maintenance Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the maintenance request "{selectedRequest?.title}"? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MaintenanceRequests; 